import { useCallback, useState } from 'react';
import { SmartSendItemApiModel, TrackingSummaryApiModel } from '~/lib/api/smartsend/types';
import TrackingProgress from './TrackingProgress';
import SmartSendInViewCheck from './SmartSendInViewCheck';
import ConvertToTrackingSummary from '../panels/ConvertToTrackingSummary';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';

interface props {
  item: SmartSendItemApiModel;
  wrapper?: (children: React.ReactNode) => React.ReactNode;
}

const HasDetails = (tracking: TrackingSummaryApiModel) =>
  !!(
    tracking &&
    (tracking.AtDepot ||
      tracking.Collected ||
      tracking.Delivered ||
      tracking.DeliveryScheduled ||
      tracking.DroppedOff ||
      tracking.InTransit ||
      tracking.MajorError)
  );

export default function LiveTrackingProgress(props: props) {
  const { getOrderTracking, getTracking } = useSmartSendApi();
  const [trackable, setTrackable] = useState(
    props.item.Type === 'SmartSend' ||
      (!!props.item.BookingDetails?.OrderLineId && !!props.item.BookingDetails?.Service?.Provider)
  );

  const [tracking, setTracking] = useState<TrackingSummaryApiModel>(
    HasDetails(props.item.TrackingSummary) ? props.item.TrackingSummary : null
  );

  const loadTracking = useCallback(async () => {
    if (!trackable || tracking?.Delivered) {
      return;
    }

    const apiResponse = props.item.BookingDetails?.OrderLineId
      ? await getOrderTracking(
          props.item.BookingDetails.Service.Provider,
          props.item.BookingDetails.Service.AccountId,
          props.item.BookingDetails.OrderLineId
        )
      : await getTracking(props.item.Id);

    if (apiResponse.success) {
      const summary = ConvertToTrackingSummary(apiResponse.result, props.item.TrackingSummary);
      if (summary) {
        setTracking(summary.summary);
        if (!summary.trackable) {
          setTrackable(false);
        }
      }
    }
  }, [getOrderTracking, getTracking, props.item, trackable, tracking?.Delivered]);

  if (tracking) {
    return props.wrapper ? (
      props.wrapper(<TrackingProgress tracking={tracking} />)
    ) : (
      <TrackingProgress tracking={tracking} />
    );
  }

  if (!trackable) {
    return null;
  }

  return <SmartSendInViewCheck inview={loadTracking} timeout={500} triggerOnce={true} />;
}
